import { HTMLAttributes, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate } from '@tanstack/react-router'
import api from '@/lib/axios'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Lütfen e-posta adresinizi giriniz' })
    .email({ message: 'Geçerli bir e-posta adresi giriniz' }),
  password: z
    .string()
    .min(1, {
      message: 'Lütfen şifrenizi giriniz',
    })
    .min(7, {
      message: 'Şifre en az 7 karakter olmalıdır',
    }),
})

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const setAccessToken = useAuthStore((s) => s.auth.setAccessToken)
  const setRefreshToken = useAuthStore((s) => s.auth.setRefreshToken)
  const setUser = useAuthStore((s) => s.auth.setUser)
  const navigate = useNavigate()

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      const res = await api.post('/auth/login', {
        email: data.email,
        password: data.password,
      })

      const { token, refreshToken, user } = res.data

      setAccessToken(token)
      setRefreshToken(refreshToken)
      setUser(user)

      toast.success('Giriş Başarılı', {
        description: `Hoşgeldin, ${user.name || user.username}`
      })
      setIsLoading(false)
      form.reset()
      navigate({ to: '/' })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Giriş başarısız'
      toast.error('Giriş Hatası', {
        description: errorMessage
      })
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>E-posta</FormLabel>
                  <FormControl>
                    <Input placeholder='kullanici@skgmetal.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <div className='flex items-center justify-between'>
                    <FormLabel>Şifre</FormLabel>
                    <Link
                      to='/sifremi-unuttum'
                      className='text-sm font-medium text-muted-foreground hover:opacity-75'
                    >
                      Şifrenizi mi unuttunuz?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className='mt-2' disabled={isLoading}>
              Giriş Yap
            </Button>


          </div>
        </form>
      </Form>
    </div>
  )
}
